import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { organisationUserRoles } from "@shared/schema";
import { User } from "@shared/schema";
import { MutationButton } from "@/components/mutation-button";

interface AllocateUserToOrgModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  organisationId: number;
}

async function fetchUsers(): Promise<User[]> {
  const response = await fetch("/api/users");
  if (!response.ok) {
    throw new Error("Failed to fetch users");
  }
  return response.json();
}

async function assignUserToOrganisation({ organisationId, userId, role }: { organisationId: number; userId: number; role: string; }) {
  const response = await fetch(`/api/organisations/${organisationId}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, role }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to assign user");
  }

  return response.json();
}

export function AllocateUserToOrgModal({ isOpen, onOpenChange, organisationId }: AllocateUserToOrgModalProps) {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [role, setRole] = useState<string>("operator");
  const [isComboboxOpen, setComboboxOpen] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: users, isLoading: isLoadingUsers } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: fetchUsers,
    enabled: isOpen,
  });

  const mutation = useMutation({
    mutationFn: assignUserToOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({
        title: "User assigned",
        description: `User ${selectedUser?.username} has been assigned to the organisation.`,
      });
      onOpenChange(false);
      setSelectedUser(null);
      setRole("operator");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null);
      setRole("operator");
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !role) {
      toast({
        title: "Missing fields",
        description: "Please select a user and a role.",
        variant: "destructive",
      });
      return;
    }
    mutation.mutate({ organisationId, userId: selectedUser.id, role });
  };
  
  const rolesToDisplay = organisationUserRoles.filter(r => r !== 'member');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Allocate User to Organisation</DialogTitle>
          <DialogDescription>
            Select an existing user and assign them a role in this organisation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="user" className="text-right">
                User
              </Label>
              <Popover open={isComboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isComboboxOpen}
                    className="col-span-3 justify-between"
                    disabled={isLoadingUsers}
                  >
                    {selectedUser
                      ? selectedUser.username
                      : "Select user..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search users..." />
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                        <CommandList>
                            {users?.map((user) => (
                                <CommandItem
                                    key={user.id}
                                    value={user.username}
                                    onSelect={() => {
                                        setSelectedUser(user);
                                        setComboboxOpen(false);
                                    }}
                                >
                                <Check
                                    className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedUser?.id === user.id ? "opacity-100" : "opacity-0"
                                    )}
                                />
                                {user.username}
                                </CommandItem>
                            ))}
                        </CommandList>
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="role" className="text-right">
                Role
              </Label>
              <Select onValueChange={setRole} defaultValue={role}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {rolesToDisplay.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <MutationButton type="submit" isLoading={mutation.isPending} loadingText="Allocating...">
              Allocate User
            </MutationButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 